import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { RefreshCw, X, Play, Pause, AlertTriangle, CheckCircle2, Calendar, TrendingUp, FolderKanban, Settings, Pencil } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import {
  listarMensalidades,
  cancelarMensalidade,
  reativarMensalidade,
  buscarMetricas,
  buscarContratoPorProjeto,
  MensalidadeItem,
  Contrato,
  Parcela,
} from "../services/financeiro";
import { confirmarDuasVezes } from "../utils/confirmarDuasVezes";
import { TodasParcelasModal, EditarContratoModal, PagamentoModal } from "./projetos/ProjetoFinanceiroSection";

const STATUS_LABEL: Record<string, string> = {
  em_dia: "Em dia",
  vence_hoje: "Vence hoje",
  atrasada: "Atrasada",
  sem_parcela: "Sem parcela",
};
const STATUS_COR: Record<string, string> = {
  em_dia: "bg-emerald-100 text-emerald-700",
  vence_hoje: "bg-amber-100 text-amber-700",
  atrasada: "bg-red-100 text-red-700",
  sem_parcela: "bg-slate-100 text-slate-500",
};

export function Assinaturas() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<"ativas" | "canceladas" | "todas">("ativas");
  const [cancelando, setCancelando] = useState<MensalidadeItem | null>(null);
  const [gerenciando, setGerenciando] = useState<MensalidadeItem | null>(null);

  if (!can(["gestor", "admin"])) {
    return <div className="text-slate-500">Acesso restrito.</div>;
  }

  const { data: mensalidades = [], isLoading } = useQuery({
    queryKey: ["mensalidades"],
    queryFn: listarMensalidades,
  });
  const { data: metricas } = useQuery({ queryKey: ["financeiro-metricas"], queryFn: buscarMetricas });

  const reativarM = useMutation({
    mutationFn: (id: string) => reativarMensalidade(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mensalidades"] });
      qc.invalidateQueries({ queryKey: ["financeiro-metricas"] });
    },
  });

  const filtradas = mensalidades.filter((m) => {
    if (filtro === "ativas") return m.ativa;
    if (filtro === "canceladas") return !m.ativa;
    return true;
  });

  const totalMrrAtivo = mensalidades.filter((m) => m.ativa).reduce((s, m) => s + Number(m.valorMensal), 0);
  const totalCanceladas = mensalidades.filter((m) => !m.ativa).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <RefreshCw size={22} className="text-blue-600" /> Assinaturas
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Mensalidades recorrentes — gerencie ativas, canceladas e MRR.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card icon={TrendingUp} color="bg-purple-100 text-purple-600" label="MRR ativo" value={`R$ ${totalMrrAtivo.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
        <Card icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" label="Ativas" value={String(mensalidades.filter((m) => m.ativa).length)} />
        <Card icon={Pause} color="bg-slate-100 text-slate-600" label="Canceladas" value={String(totalCanceladas)} />
        <Card icon={Calendar} color="bg-blue-100 text-blue-600" label="A receber 30d" value={`R$ ${(metricas?.aReceber30d ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {(["ativas", "canceladas", "todas"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFiltro(f)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md ${
              filtro === f ? "bg-white text-blue-600 shadow-sm" : "text-slate-600 hover:text-slate-800"
            }`}
          >
            {f === "ativas" ? "Ativas" : f === "canceladas" ? "Canceladas" : "Todas"}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <p className="text-center py-8 text-slate-400">Carregando…</p>
        ) : filtradas.length === 0 ? (
          <p className="text-center py-8 text-slate-400">Nenhuma assinatura {filtro === "ativas" ? "ativa" : filtro === "canceladas" ? "cancelada" : ""} encontrada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">Projeto / Cliente</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500">Mensalidade</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-500">Vence dia</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-500">Início</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-500">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtradas.map((m) => (
                  <tr key={m.id} className={`hover:bg-slate-50 ${!m.ativa ? "opacity-60" : ""}`}>
                    <td className="px-4 py-3">
                      <Link to={`/projetos/${m.projetoId}`} className="font-medium text-slate-800 hover:text-blue-600 flex items-center gap-1.5">
                        <FolderKanban size={12} /> {m.projeto.nome}
                      </Link>
                      <p className="text-xs text-slate-500">{m.projeto.cliente}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">R$ {Number(m.valorMensal).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-center">{m.diaVencimento ?? "—"}</td>
                    <td className="px-4 py-3 text-center text-xs text-slate-500">
                      {m.inicioCobranca ? new Date(m.inicioCobranca).toLocaleDateString("pt-BR") : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {m.ativa ? (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COR[m.status]}`}>
                          {STATUS_LABEL[m.status] || m.status}
                          {m.status === "atrasada" && ` (${m.diasAtraso}d)`}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600">Cancelada</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => setGerenciando(m)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                          title="Editar configuração e ver cobranças"
                        >
                          <Settings size={12} /> Gerenciar
                        </button>
                        {m.ativa ? (
                          <button
                            onClick={() => setCancelando(m)}
                            className="text-xs text-red-600 hover:text-red-700 font-medium inline-flex items-center gap-1"
                          >
                            <Pause size={12} /> Cancelar
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              const ok = confirmarDuasVezes({
                                primeiraMensagem: `Reativar a assinatura "${m.projeto.nome}"?`,
                                segundaMensagem: `Confirmar reativação? Novas cobranças voltarão a ser geradas.`,
                              });
                              if (ok) reativarM.mutate(m.id);
                            }}
                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center gap-1"
                          >
                            <Play size={12} /> Reativar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cancelando && (
        <CancelarModal
          mensalidade={cancelando}
          onClose={() => setCancelando(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["mensalidades"] });
            qc.invalidateQueries({ queryKey: ["financeiro-metricas"] });
            setCancelando(null);
          }}
        />
      )}

      {gerenciando && (
        <GerenciarAssinaturaModal
          mensalidade={gerenciando}
          onClose={() => setGerenciando(null)}
        />
      )}
    </div>
  );
}

/**
 * Drawer/modal de gerenciamento de uma assinatura: cards com resumo,
 * botão pra editar config e tabela de cobranças com ações inline.
 * Reaproveita os 3 modais que já existem dentro do projeto.
 */
function GerenciarAssinaturaModal({ mensalidade, onClose }: {
  mensalidade: MensalidadeItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [showEditar, setShowEditar] = useState(false);
  const [pagandoParcela, setPagandoParcela] = useState<Parcela | null>(null);
  const [verCobrancas, setVerCobrancas] = useState(false);

  const { data: contrato, isLoading } = useQuery({
    queryKey: ["contrato-projeto", mensalidade.projetoId],
    queryFn: () => buscarContratoPorProjeto(mensalidade.projetoId),
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["contrato-projeto", mensalidade.projetoId] });
    qc.invalidateQueries({ queryKey: ["mensalidades"] });
    qc.invalidateQueries({ queryKey: ["parcelas-vencidas"] });
    qc.invalidateQueries({ queryKey: ["financeiro-metricas"] });
  };

  const totalPago = (contrato?.parcelas || []).reduce((s, p) => s + (Number(p.valorPago) || 0), 0);
  const pagas = (contrato?.parcelas || []).filter((p) => p.status === "pago").length;
  const total = (contrato?.parcelas || []).length;
  const proximaPendente = (contrato?.parcelas || [])
    .filter((p) => p.status === "pendente" || p.status === "atrasado" || p.status === "parcial")
    .sort((a, b) => new Date(a.vencimento).getTime() - new Date(b.vencimento).getTime())[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <Settings size={16} /> {mensalidade.projeto.nome}
            </h3>
            <p className="text-xs text-slate-500 truncate">{mensalidade.projeto.cliente}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isLoading || !contrato ? (
            <p className="text-sm text-slate-400 text-center py-6">Carregando…</p>
          ) : (
            <>
              {/* Cards de resumo */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <Resumo label="Mensal" valor={`R$ ${Number(mensalidade.valorMensal).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
                <Resumo label="Recebido" valor={`R$ ${totalPago.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} cor="text-emerald-700" />
                <Resumo label="Parcelas pagas" valor={`${pagas} / ${total}`} />
                <Resumo
                  label="Próxima"
                  valor={proximaPendente ? new Date(proximaPendente.vencimento).toLocaleDateString("pt-BR") : "—"}
                />
              </div>

              {/* Configuração atual */}
              <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-700 dark:text-slate-200">Configuração</p>
                  <button
                    onClick={() => setShowEditar(true)}
                    className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                  >
                    <Pencil size={12} /> Editar
                  </button>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  Vence todo dia <strong>{mensalidade.diaVencimento || "—"}</strong> ·
                  Início: <strong>{mensalidade.inicioCobranca ? new Date(mensalidade.inicioCobranca).toLocaleDateString("pt-BR") : "—"}</strong>
                  {mensalidade.fimCobranca && (
                    <> · Fim: <strong>{new Date(mensalidade.fimCobranca).toLocaleDateString("pt-BR")}</strong></>
                  )}
                </p>
              </div>

              {/* Lista compacta de cobranças (últimas 5) + botão "ver todas" */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-sm text-slate-700 dark:text-slate-200">Cobranças recentes</p>
                  <button
                    onClick={() => setVerCobrancas(true)}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Ver todas ({total})
                  </button>
                </div>
                {contrato.parcelas.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-3 text-center">Nenhuma cobrança gerada ainda.</p>
                ) : (
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    {contrato.parcelas
                      .slice()
                      .sort((a, b) => new Date(b.vencimento).getTime() - new Date(a.vencimento).getTime())
                      .slice(0, 5)
                      .map((p) => (
                        <div key={p.id} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 border-slate-100 dark:border-slate-800 text-xs">
                          <span className="font-mono text-slate-400">#{p.numero}</span>
                          <span className="text-slate-700 dark:text-slate-200">{new Date(p.vencimento).toLocaleDateString("pt-BR")}</span>
                          <span className="ml-auto tabular-nums">R$ {Number(p.valor).toFixed(2)}</span>
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                            p.status === "pago" ? "bg-emerald-100 text-emerald-700" :
                            p.status === "atrasado" ? "bg-red-100 text-red-700" :
                            p.status === "parcial" ? "bg-blue-100 text-blue-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>{p.status}</span>
                          {(p.status === "pendente" || p.status === "atrasado" || p.status === "parcial") && (
                            <button
                              onClick={() => setPagandoParcela(p)}
                              className="text-[10px] bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded"
                            >
                              Pagar
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <Link to={`/projetos/${mensalidade.projetoId}`} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <FolderKanban size={12} /> Abrir projeto
          </Link>
          <button onClick={onClose} className="ml-auto px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-100">
            Fechar
          </button>
        </div>
      </div>

      {/* Sub-modais reusados do projeto */}
      {showEditar && contrato && (
        <EditarContratoModal
          contrato={contrato as Contrato}
          onClose={() => setShowEditar(false)}
          onSuccess={() => { invalidar(); setShowEditar(false); }}
        />
      )}
      {verCobrancas && contrato && (
        <TodasParcelasModal
          contrato={contrato as Contrato}
          onClose={() => setVerCobrancas(false)}
          onPagar={(p) => { setVerCobrancas(false); setPagandoParcela(p); }}
        />
      )}
      {pagandoParcela && (
        <PagamentoModal
          parcela={pagandoParcela}
          onClose={() => setPagandoParcela(null)}
          onSuccess={() => { invalidar(); setPagandoParcela(null); }}
        />
      )}
    </div>
  );
}

function Resumo({ label, valor, cor }: { label: string; valor: string; cor?: string }) {
  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5">
      <p className="text-[10px] text-slate-500 uppercase font-medium">{label}</p>
      <p className={`font-semibold tabular-nums truncate ${cor || "text-slate-800 dark:text-slate-100"}`} title={valor}>
        {valor}
      </p>
    </div>
  );
}

function Card({ icon: Icon, color, label, value }: { icon: any; color: string; label: string; value: string }) {
  const tam = value.length <= 10
    ? "text-base"
    : value.length <= 14
      ? "text-sm"
      : "text-xs";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center shrink-0`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500 truncate">{label}</p>
        <p className={`font-bold text-slate-800 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis ${tam}`} title={value}>
          {value}
        </p>
      </div>
    </div>
  );
}

function CancelarModal({ mensalidade, onClose, onSuccess }: {
  mensalidade: MensalidadeItem;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [dataFim, setDataFim] = useState(new Date().toISOString().split("T")[0]);

  const cancelarM = useMutation({
    mutationFn: () => cancelarMensalidade(mensalidade.id, motivo, dataFim),
    onSuccess,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" /> Cancelar assinatura
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700">
            Você está cancelando a mensalidade do projeto <strong>{mensalidade.projeto.nome}</strong> ({mensalidade.projeto.cliente}).
          </p>
          <p className="text-xs text-slate-500">
            Parcelas pendentes com vencimento <strong>após</strong> a data fim serão marcadas como canceladas.
            Parcelas já vencidas/pagas mantêm-se como estão.
          </p>
          <div>
            <label className="text-xs text-slate-600">Data fim das cobranças</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Motivo do cancelamento (opcional)</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              placeholder="Ex.: Cliente solicitou via e-mail em DD/MM"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-red-500"
            />
          </div>
          {(cancelarM.error as any)?.response?.data?.error?.message && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {(cancelarM.error as any).response.data.error.message}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Voltar</button>
          <button
            onClick={() => {
              const ok = confirmarDuasVezes({
                primeiraMensagem: `Cancelar assinatura de "${mensalidade.projeto.nome}"?`,
                segundaMensagem: `CONFIRMAR cancelamento? Cobranças futuras serão paradas a partir de ${new Date(dataFim).toLocaleDateString("pt-BR")}.`,
              });
              if (ok) cancelarM.mutate();
            }}
            disabled={cancelarM.isPending}
            className="px-4 py-1.5 rounded text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {cancelarM.isPending ? "Cancelando…" : "Cancelar assinatura"}
          </button>
        </div>
      </div>
    </div>
  );
}
